import type { Partner } from "@expert/shared-types";

import { partners } from "@expert/shared-types";

import { type CctsContact, type Contact, vdnToPartner } from "./transfer";

export type { Partner } from "@expert/shared-types";

export interface PartnerProgram {
    name: string;
    claimEligible: boolean;
    survey: {
        generalSurveyId: string;
        claimsSurveyId: string;
    };
}

/** Orbit groups partner mapping */
export const PartnerMapping: ReadonlyMap<string, Partner> = new Map<string, Partner>()
    .set("ADT ExWo", "adt")
    .set("ATT MDPP ExWo", "att")
    .set("ATT MI Exwo", "att")
    .set("ATT MPP ExWo", "att")
    .set("ATTMessaging", "att")
    .set("CH Small Clients ExWo", "others")
    .set("EE UK ExWo", "others")
    .set("Home Plus ExWo", "others")
    .set("Life360 ExWo", "life360")
    .set("Optimum ExWo", "others")
    .set("Pilot ExWo", "others")
    .set("PSS Staples ExWo", "others")
    .set("TMO ExWo", "tmobile")
    .set("Verizon Care ExWo", "verizon")
    .set("Verizon ExWo", "verizon")
    .set("VerizonMessaging", "verizon")
    .set("VerizonSaves", "verizon")
    .set("VerizonFiosMessaging", "fios")
    .set("Verizon Fios ExWo", "fios")
    .set("XBOX ExWo", "microsoft")
    .set("VHDP ExWo", "verizonconnectedhome")
    .set("ATT HomeTech ExWo", "attconnectedhome")
    .set("Youtube Messaging", "youtubetv")
    .set("YoutubeTVMessaging", "youtubetv");

export function isValidPartner(value: string | undefined): value is Partner {
    if (!value) {
        return false;
    }
    return (partners as readonly string[]).includes(value);
}

export function getContactPartner(contact: Contact | CctsContact): Partner | undefined | "NOT FOUND" {
    return vdnToPartner.get(contact.vdn) ?? contact.partner;
}

export function filterContacts(contacts: Contact[], partner: Partner): Contact[] {
    return contacts.filter(
        // If contact does not have a partner, or is NOT FOUND, then it is available
        // for all experts to use.
        (contact) =>
            !contact.partner ||
            contact.partner === partner ||
            contact.partner === "NOT FOUND" ||
            contact.partner === "asurion",
    );
}

export function formatPartnerName(partner: Partner): string {
    switch (partner) {
        case "verizon":
            return "Verizon";
        case "att":
            return "AT&T";
        case "cricket":
            return "Cricket";
        case "aafes":
            return "AAFES";
        case "adt":
            return "ADT";
        case "amazonrouter":
            return "Amazon Router";
        case "comcastbusiness":
            return "Comcast Business";
        case "mcafeesecurityscan":
            return "McAfee Security Scan";
        case "mcafeetechsupport":
            return "McAfee Tech Support";
        case "asurion":
            return "Asurion";
        case "asurionhomeplus":
            return "Asurion Home+";
        case "asurionphonecare":
            return "Asurion Phone Care";
        case "bluestream":
            return "Blue Stream";
        case "brightspeed":
            return "Brightspeed";
        case "cactus":
            return "Cactus";
        case "cox":
            return "Cox";
        case "dish":
            return "Dish";
        case "ee":
            return "EE";
        case "frontier":
            return "Frontier";
        case "hotwire":
            return "Hotwire";
        case "hpsales":
            return "HP Sales";
        case "koodo":
            return "Koodo";
        case "leakprotect":
            return "Leak Protect";
        case "liberty":
            return "Liberty";
        case "mediacom":
            return "Mediacom";
        case "nexcom":
            return "Nexcom";
        case "pilot":
            return "Pilot";
        case "samsungna":
            return "Samsung";
        case "sparklight":
            return "Sparklight";
        case "sparklightsales":
            return "Sparklight Sales";
        case "staples":
            return "Staples";
        case "stapleshome":
            return "Staples Home";
        case "staplessmb":
            return "Staples SMB";
        case "telus":
            return "Telus";
        case "telushomepro":
            return "Telus HomePro";
        case "thd":
            return "THD";
        case "thesource":
            return "The Source";
        case "tmobile":
            return "T-Mobile";
        case "unknown":
            return "Unknown";
        case "uscellular":
            return "US Cellular";
        case "viasat":
            return "Viasat";
        case "fios":
            return "Fios";
        case "microsoft":
            return "Microsoft";
        case "life360":
            return "Life360";
        case "optimum":
            return "Optimum";
        case "reach":
            return "Reach";
        case "tracfone":
            return "Tracfone";
        case "dell":
            return "Dell";
        case "nrg":
            return "NRG";
        case "smartpay":
            return "SmartPay";
        case "asuriontechcare":
            return "Asurion Tech Care";
        case "attconnectedhome":
            return "AT&T Connected Home";
        case "optimumbiz":
            return "Optimum Business";
        case "verizonconnectedhome":
            return "Verizon Connected Home";
        case "verizonwholeoffice":
            return "Verizon Whole Office";
        case "vzwsmb":
            return "Verizon SMB";
        case "xboxcareplus":
            return "Xbox Care Plus";
        case "yahoo":
            return "Yahoo";
        case "youtubetv":
            return "Youtube TV";
        default:
            return partner;
    }
}
